import { compact, compactFlatten, flatten, isArray, isPlainObject, objectHasKeys } from '@art-suite/art-core-ts';

export const pureFunctionalMap = <T>(array: T[] | undefined, callback: (item: T, index: number) => T): T[] => {
  if (!array) return [];
  let toReturn: T[] | undefined = undefined;
  for (let i = 0; i < array.length; i++) {
    const item = array[i];
    const result = callback(item, i);
    if (result !== item) {
      if (!toReturn) toReturn = array.slice(); // clone the entire array
      toReturn[i] = result;
    }
  }
  return toReturn ?? array;
};

/**
 * Merge updates into a, returning a new object if the updates are different, otherwise returning the original object.
 *
 * - undefineds updates are ignored.
 * - null updates are treated as deletions.
 */
export const pureFunctionalMerge = <T>(a: T, updates: Partial<T>): T => {
  // determine if the updates are actually different
  for (const key in updates) {
    const updateValue = updates[key];
    if (updateValue === undefined) continue; // undefined is a no-op
    const currentValue = a[key];
    if (updateValue === null && currentValue == null) continue; // null is a deletion, so ignore it if the current value is undefined
    if (currentValue !== updateValue) {
      // difference detected, return a new object following the merge rules
      const result = {};
      // init result with the current values except for deleted values signaled by null in the updates
      for (const key in a) {
        const currentValue = a[key];
        const updateValue = updates[key];
        if (currentValue != null && updateValue === null) continue; // null is a deletion, so skip the value
        (result as any)[key] = currentValue;
      }
      // add the new values
      for (const key in updates) {
        const updateValue = updates[key];
        if (updateValue == null) continue; // skip undefined updates as per spec; skip nulls here as they are already accounted for
        (result as any)[key] = updateValue;
      }
      return result as T;
    }
  }
  return a;
};

/**
 * Map over a record's values, returning a new record if any values changed, otherwise returning the original record.
 *
 * - null and undefined are treated as identical (both mean "no value")
 * - The result will never contain null or undefined values (keys with null/undefined values are omitted)
 * - This function is optimized to avoid creating objects until a change is detected
 */
export const pureFunctionalRecordMap = <T extends Record<string, any>>(
  record: T | undefined,
  callback: <K extends keyof T>(value: T[K], key: K) => T[K],
): T => {
  if (!record) return {} as T;
  const keys = Object.keys(record) as (keyof T)[];
  let toReturn: Record<string, any> | undefined = undefined;

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const value = record[key];
    const result = callback(value, key);

    const valueIsNullish = value == null;
    const resultIsNullish = result == null;

    if (valueIsNullish || result !== value) {
      if (!toReturn) {
        toReturn = {};
        for (let j = 0; j < i; j++) {
          const previousKey = keys[j];
          const previousValue = record[previousKey];
          toReturn[previousKey as string] = previousValue; // copy the previous, unchanged items (skip nullish values)
        }
      }
      if (!resultIsNullish) {
        toReturn[key as string] = result; // only add non-nullish values
      }
    } else if (toReturn && !valueIsNullish) {
      toReturn[key as string] = value; // copy unchanged non-nullish items after a change was detected
    }
  }

  return (toReturn ?? record) as T;
};

export function pureFunctionalRecordOrArrayMap<T extends any[]>(
  recordOrArray: T,
  callback: (item: T[number], index: number) => T[number],
): T;
export function pureFunctionalRecordOrArrayMap<T extends Record<string, any>>(
  recordOrArray: T,
  callback: <K extends keyof T>(value: T[K], key: K) => T[K],
): T;

/**
 * Map over a record or array, returning a new record or array if any values changed, otherwise returning the original record or array.
 *
 * - no special interpretation of null or undefined - whatever you return is what gets stored
 * - This function is performance optimized to avoid creating objects until a change is detected
 */
export function pureFunctionalRecordOrArrayMap<T extends Record<string, any> | any[]>(
  recordOrArray: T,
  callback: any,
): T {
  if (Array.isArray(recordOrArray)) {
    return pureFunctionalMap(recordOrArray, callback) as T;
  }
  return pureFunctionalRecordMap(recordOrArray, callback) as T;
}

/**
 * Options for the deepPureFunctionalMap function. If both compact and compactFlatten are true, compactFlatten will used.
 *
 * - compact: if true, any post-mapped array containing nulls or undefineds will be compacted (i.e. nulls and undefineds will be removed)
 * - flatten: if true, any post-mapped array containing nested arrays will be flattened (i.e. nested arrays will be inlined recursively and the result will be a single level array).
 * - compactFlatten: if true, OR compact AND flatten are true, any post-mapped array will be compacted and flattened (pure functionally
 * - stripEmptyContainers: if true, any post-mapped empty-array or object ([] or {}) will be replaced with undefined
 *
 * Note: all options are pure-functional - if nothing changed, the original value is always returned
 */
export type DeepPureFunctionalMapOptions = {
  compact?: boolean;
  flatten?: boolean;
  compactFlatten?: boolean;
  stripEmptyContainers?: boolean;
};

const noopCallback = (value: any, key?: any) => value;

/**
 * Map over a record or array or singleton value recursively, invoking the callback on each non-array, non-object value, returning a new, parallel data structure pure functionally. i.e. if nothing changed, the original value is always returned; if some parts of the structure changed but not others, only the parent-tree of the changed parts contains new objects/arrays, the other, unchanged parts of the structure are returned as-is.
 *
 * - This function is performance optimized
 * - call back will receive the index (number) or key (string) of the current value if it is in the context of an array or record, respectively; if the very root value is a singleton, it will be undefined
 * - See the DeepPureFunctionalMapOptions for options that control the behavior of the mapping and post-mapping transformations
 *
 * Example: simplify JSON data structure and round any numbers to the nearest integer before serializing to JSON
 *
 * ```
 * const data = {
 *   a: 1.56,
 *   b: [2, 3, 4, {}, null, "hello"],
 *   c: { d: 5, e: 6.2, f: [] },
 *   d: undefined
 * };
 * const simplified = deepPureFunctionalMap(
 *   data,
 *   (v: any) => isNumber(v) ? Math.round(v) : v,
 *   { compactFlatten: true, stripEmptyContainers: true }
 * );
 * console.log(simplified); // { a: 2, b: [2, 3, 4, "hello"], c: { d: 5, e: 6 } }
 * ```
 *
 * TypeScript Note: assumes but does not verify that the type signature of the elements won't change when mapped.
 */
export function deepPureFunctionalMap<T extends Record<string, any> | any[] | any>(
  recordOrArray: T,
  callback: (value: any, key?: any) => any = noopCallback,
  options?: DeepPureFunctionalMapOptions,
): T {
  if (isArray(recordOrArray)) {
    let result: any = pureFunctionalMap(recordOrArray, v => deepPureFunctionalMap(v, callback, options));
    if (options?.compactFlatten || (options?.compact && options?.flatten)) result = compactFlatten(result);
    else if (options?.flatten) result = flatten(result);
    else if (options?.compact) result = compact(result);
    if (options?.stripEmptyContainers && result.length === 0) result = undefined;
    return result as T;
  } else if (isPlainObject(recordOrArray)) {
    let result: any = pureFunctionalRecordMap(recordOrArray, (value, key) => deepPureFunctionalMap(value, callback, options));
    if (options?.stripEmptyContainers && !objectHasKeys(result)) result = undefined;
    return result as T;
  } else {
    return callback(recordOrArray) as T;
  }
}
